import * as React from 'react';

import type {
  AudienceDelta,
  CohortAudience,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useAudienceLearnerEditor } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearnerEditor';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortLearnerList } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearnerList';
import useLearnerList from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';

/**
 * What to persist when the admin saves, discriminated by entry point:
 *  - `filter`: a removals-only delta against the server cohort (auto-generate
 *    flow); the (future) BE filter-regeneration API will consume it.
 *  - `list`: the in-memory working copy (manual flow).
 */
export type LearnerListSaveResult = { kind: 'filter'; delta: AudienceDelta } | { kind: 'list'; learners: Learner[] };

type Params = {
  /** Cohort the editor opens with — the in-memory working copy starts here. */
  initialLearners: Learner[];
  /**
   * Filter-backed entry point: the pool is fetched server-side from this ready
   * `{ orgId, filter }` audience and edits are tracked as a delta. Absent for
   * the manual flow.
   */
  audience?: LearnerListAudience;
  /** NLQ cohort audience for the AI compose flow. */
  cohortAudience?: CohortAudience;
  /** Delta to seed the editor with when re-opening (filter flow). */
  initialDelta?: AudienceDelta;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  /** Total rows matched by the current query (across all pages). */
  filteredCount: number;
  loading: boolean;
  /** True when the (server) page fetch failed. */
  error: boolean;
  /** Re-include / add a learner (by row). */
  addLearner: (learner: Learner) => void;
  /** Remove a learner from the selection (by id). */
  removeLearner: (id: string) => void;
  /** The payload to persist on Save, discriminated by entry point. */
  save: LearnerListSaveResult;
};

/**
 * Single source of truth for *how each entry point* sources, searches, and edits
 * the learner list behind the "Add or remove learners" screen. The screen stays
 * presentational — it renders `pageRows` and calls `addLearner` / `removeLearner`
 * without knowing whether the data is server-fetched or in-memory.
 *
 * Three entry points exist today:
 *  - **Filter:** `audience` present. The cohort is fetched page-by-page from
 *    the gateway and edits are a removals-only delta.
 *  - **Cohort:** `cohortAudience` present. The cohort is fetched from the
 *    EnterpriseLearnerCohort API; edits are a removals-only delta.
 *  - **Manual:** no audience. The working copy is an in-memory list.
 */
export const useLearnerListEntryPoint = ({
  initialLearners,
  audience,
  cohortAudience,
  initialDelta,
}: Params): Result => {
  const isServer = Boolean(audience);
  const isCohort = Boolean(cohortAudience);

  // --- Manual (in-memory) entry point ---
  const [editedList, setEditedList] = React.useState<Learner[]>(initialLearners);
  const clientEditor = useLearnerList({ list: editedList });

  // --- Filter (server-backed) entry point ---
  const serverEditor = useAudienceLearnerEditor({ audience, initialDelta });

  // --- Cohort (NLQ-backed) entry point ---
  // Uses the cohort fetch API for pagination/search; edits tracked as delta.
  const cohortEditor = useCohortLearnerList({ cohortAudience });
  const [cohortRemovedIds, setCohortRemovedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.removedIds));

  const addLearner = React.useCallback(
    (learner: Learner) => {
      if (isCohort) {
        setCohortRemovedIds((prev) => {
          const next = new Set(prev);
          next.delete(learner.id);
          return next;
        });
      } else if (isServer) {
        serverEditor.addLearner(learner.id);
      } else {
        setEditedList((previous) =>
          previous.some((existing) => existing.id === learner.id) ? previous : [...previous, learner]
        );
      }
    },
    [isCohort, isServer, serverEditor]
  );

  const removeLearner = React.useCallback(
    (id: string) => {
      if (isCohort) {
        setCohortRemovedIds((prev) => new Set(prev).add(id));
      } else if (isServer) {
        serverEditor.removeLearner(id);
      } else {
        setEditedList((previous) => previous.filter((learner) => learner.id !== id));
      }
    },
    [isCohort, isServer, serverEditor]
  );

  // Derive page rows for the cohort editor with removal overlay.
  const cohortPageRows: LearnerRow[] = React.useMemo(
    () => cohortEditor.pageRows.map((row) => ({ ...row, inList: !cohortRemovedIds.has(row.learner.id) })),
    [cohortEditor.pageRows, cohortRemovedIds]
  );

  const cohortDelta: AudienceDelta = React.useMemo(
    () => ({ removedIds: [...cohortRemovedIds], addedIds: [] }),
    [cohortRemovedIds]
  );

  // The active source supplies the common list/search/pagination surface.
  if (isCohort) {
    const save: LearnerListSaveResult = { kind: 'filter', delta: cohortDelta };
    return {
      searchQuery: cohortEditor.searchQuery,
      setSearchQuery: cohortEditor.setSearchQuery,
      page: cohortEditor.page,
      setPage: cohortEditor.setPage,
      pageCount: cohortEditor.pageCount,
      pageRows: cohortPageRows,
      filteredCount: cohortEditor.filteredCount,
      loading: cohortEditor.loading,
      error: cohortEditor.error,
      addLearner,
      removeLearner,
      save,
    };
  }

  const active = isServer ? serverEditor : clientEditor;

  const save: LearnerListSaveResult = isServer
    ? { kind: 'filter', delta: serverEditor.delta }
    : { kind: 'list', learners: editedList };

  return {
    searchQuery: active.searchQuery,
    setSearchQuery: active.setSearchQuery,
    page: active.page,
    setPage: active.setPage,
    pageCount: active.pageCount,
    pageRows: active.pageRows,
    filteredCount: active.filteredCount,
    loading: isServer ? serverEditor.loading : false,
    error: isServer ? serverEditor.error : false,
    addLearner,
    removeLearner,
    save,
  };
};

export default useLearnerListEntryPoint;
